<script setup lang="ts">
import { capitalize, get } from 'lodash'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  VBadge,
  VButton,
  VTable,
  VModal,
  VSection,
  VTextField,
  VTextArea,
  ActionsMenu,
  ActionsGroup,
  ActionItem,
} from '@/modules/shared/components'
import { Option } from '@/modules/shared/utils/form'
import { useDocumentStore } from '../stores/document-store'
import { useTemplateStore } from '@/modules/signing/stores/template-store'
import { format, unflattenKey } from '@/modules/shared/utils/v-table'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { Signing } from 'pandadoc-signing'
import { useModal } from '@/modules/shared/composables/use-modal'
import { Editor, BlockKind, FieldKind, UIElement } from 'pandadoc-editor'
import EmbeddedEditorModal from '@/modules/signing/components/EmbeddedEditorModal.vue'
import EditTemplateDetailsModal from '@/modules/signing/components/EditTemplateDetailsModal.vue'
import EmbeddedSigningModal from '@/modules/signing/components/EmbeddedSigningModal.vue'
import TheUploader from '@/modules/signing/components/the-uploader.vue'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'
import { useRoute } from 'vue-router'
import { useEntityStore } from '../stores/entity-store'
import { isEntityAdmin } from '../utils/entity'

const { t } = useI18n()
const route = useRoute()

const props = defineProps<{
  investor_type: 'spv' | 'fund' | 'gp' | 'individual' | 'funding-entity' | 'group' | 'all'
  investor_id: string | number
  filterOptions?: Option[]
  selected_keys?: string[]
  skeleton?: boolean
}>()

const internal_skeleton = ref(true)
const authStore = useAuthStore()
const documentStore = useDocumentStore()
const templateStore = useTemplateStore()

const skeleton = computed(() => props.skeleton || internal_skeleton.value)
const filterOptions = computed(() => props.filterOptions || [])
const selected_keys = computed(() => props.selected_keys || [])
const notificationStore = useNotificationStore()
const entityStore = useEntityStore()

// Entity admin check helpers
const currentUserCid = computed(() => `individual:${authStore.current_user.investor_id}`)
const currentEntity = computed(() => {
  const entityCid = `${props.investor_type}:${props.investor_id}`
  return entityStore.items.get(entityCid)
})
const checkIsEntityAdmin = computed(() => {
  if (!currentEntity.value || !authStore.current_user.investor_id) return false
  try {
    return isEntityAdmin(currentEntity.value, currentUserCid.value)
  } catch {
    return false
  }
})

function can(block, action, item?) {
  switch (block) {
    case 'template':
      if (['request-signature', 'build-template', 'view', 'action-use', 'action-delete', 'edit'].includes(action)) {
        return (
          !['individual', 'funding-entity'].includes(props.investor_type) &&
          (authStore.is_site_or_group_admin || checkIsEntityAdmin.value)
        )
      }
      if (['action-edit'].includes(action)) {
        return (
          !['individual', 'funding-entity'].includes(props.investor_type) &&
          (authStore.is_site_or_group_admin || checkIsEntityAdmin.value)
        )
      }
      break
    case 'document':
      if ('sign' === action && item) {
        return unflattenKey(item, 'signatures').find((signature) => {
          const isMe = signature.signer.id === authStore.current_user.investor_id
          if (!isMe) return false

          // No signing order → allow immediately
          if (!signature.signing_order) return true

          // Has signing order → must be current signer or already signed
          return signature.current_signer || signature.status_code === 'signed'
        })
      }
      if ('view' === action && item) {
        if (authStore.is_site_or_group_admin) return true
        if (checkIsEntityAdmin.value) return true
        const managedSetIds = authStore.current_user?.managed_investor_set_ids ?? []
        if (managedSetIds.length > 0) {
          const hasAdminedEntity = unflattenKey(item, 'signatures').some((sig) =>
            managedSetIds.includes(String(sig.investor?.id)),
          )
          if (hasAdminedEntity) return true
        }
        return unflattenKey(item, 'signatures').some((signature) => {
          return signature.signer?.id === authStore.current_user?.investor_id
        })
      }
      if (['void'].includes(action)) return authStore.is_site_or_group_admin
      break
    case 'investor':
      if (['view'].includes(action)) return authStore.is_site_or_group_admin
      break
    default:
      break
  }
  return false
}

const requestSignatureUrl = computed(() => {
  let url = null
  switch (props.investor_type) {
    case 'spv':
    case 'fund':
    case 'gp':
      url = { name: 'investing.entity.document.new' }
      break
    case 'group':
      url = { name: 'investing.document.new' }
      break
    default:
      break
  }
  return url
})

// TEMPLATES
const templates = computed(() =>
  templateStore.templates.map((template) => {
    return { ...template, provider_template_cache: null }
  }),
)

// Check if an awaiting_signature document exists for a template
const hasAwaitingDocument = (item) => {
  const template = templateStore.template_items.get(item.id)
  if (!template) return false

  // Check if any awaiting document matches this template's provider_template_id
  return documentStore.documents.some((doc) => {
    return doc.provider_template_id === template.provider_template_id && doc.status === 'awaiting'
  })
}

// Check if the latest non-voided document for any of the given provider_template_ids is completed
const hasCompletedDocument = (providerTemplateIds: string[]) => {
  if (!providerTemplateIds || providerTemplateIds.length === 0) return false

  // Find all non-voided documents matching any version of this template
  const templateDocs = documentStore.documents.filter(
    (doc) => providerTemplateIds.includes(doc.provider_template_id) && doc.status !== 'voided',
  )

  if (templateDocs.length === 0) return false

  // Get the most recent document
  const latestDoc = templateDocs.reduce((latest, doc) => {
    return new Date(doc.date) > new Date(latest.date) ? doc : latest
  })

  return latestDoc.status === 'completed'
}

// Check if template's dependency is satisfied
const isDependencySatisfied = (item) => {
  const template = templateStore.template_items.get(item.id)
  if (!template) return true // No template info = no dependency check
  if (!template.dependency_template_ids?.length) return true // No dependency = satisfied

  return hasCompletedDocument(template.dependency_template_ids)
}

// Check if template row should be disabled/grayed out
const isTemplateDisabled = (item) => {
  const template = templateStore.template_items.get(item.id)
  if (!template?.is_global) return false // Only global templates can be disabled

  return !isDependencySatisfied(item)
}

// Create onboarding document for global templates (Series Supplement, AML)
const createOnboardingDocument = async (item) => {
  const template = templateStore.template_items.get(item.id)
  if (!template) return

  loadingOnboardingTemplateIds.value.push(item.id)
  try {
    await templateStore.createOnboardingDocument(route.params.entity_id, template.provider_template_id)
    // Refresh documents list
    await documentStore.fetchDocuments(props.investor_type, props.investor_id)
  } finally {
    loadingOnboardingTemplateIds.value = loadingOnboardingTemplateIds.value.filter((id) => id !== item.id)
  }
}

const removeTemplate = async (item) => {
  await templateStore.removeTemplate(item.id)
  await templateStore.fetchTemplates(props.investor_type, props.investor_id)
}

const generateUseTemplateUrl = (item) => {
  const template = templateStore.template_items.get(item.id)
  let url = null
  switch (props.investor_type) {
    case 'spv':
    case 'fund':
    case 'gp':
      url = { name: 'investing.entity.document.new', query: { provider_template_id: template.provider_template_id } }
      break
    case 'group':
      url = { name: 'investing.document.new', query: { provider_template_id: template.provider_template_id } }
      break
    default:
      break
  }
  return url
}

// DOCUMENTS
const selectedDocumentType = ref('in-progress')
const selectDocumentType = (type: string) => {
  selectedDocumentType.value = type
}

const currentDocuments = computed(() =>
  documentStore.documents
    .filter((document) => {
      if (selectedDocumentType.value === 'in-progress') {
        return document.status === 'awaiting'
      }
      if (selectedDocumentType.value === 'completed') {
        return document.status === 'completed'
      }
      if (selectedDocumentType.value === 'voided') {
        return document.status === 'voided'
      }
    })
    .filter((document) => {
      if (selected_keys.value.length === filterOptions.value.length) return true
      return document.signatures.some((signature) => selected_keys.value.includes(signature.investor._cid))
    }),
)

const document_count = computed(() => {
  let in_progress = 0
  let completed = 0
  let voided = 0

  documentStore.documents?.forEach((document) => {
    if (document.status === 'awaiting') in_progress++
    if (document.status === 'completed') completed++
    if (document.status === 'voided') voided++
  })

  return { in_progress, completed, voided }
})

const getDocumentStatus = (item) => {
  const document = documentStore.items.get(item.id)
  if (document.status === 'voided') return `Voided ${format(document.voided_at, 'date')}`
  const no_of_signed = document.signatures.filter((signature) => signature.status === 'Signed').length

  return `${no_of_signed} / ${document.signatures.length} signatures received`
}

const voidDocument = async (item) => {
  await documentStore.voidDocument(item.id)
  await documentStore.fetchDocuments(props.investor_type, props.investor_id)
}

const signatureSendReminder = async (item) => {
  loadingReminderSignatureIds.value.push(item.id)
  try {
    await documentStore.signatureSendReminder(item.id)
    await documentStore.fetchDocuments(props.investor_type, props.investor_id)
  } finally {
    loadingReminderSignatureIds.value = loadingReminderSignatureIds.value.filter((id) => id !== item.id)
  }
}

const getSigners = (item) => {
  const signers = documentStore.items
    .get(item.id)
    .signatures.map((signature) => signature.investor?.name || signature.signer?.name)
  return signers.join(', ')
}

// Panda Doc Editor and Signing Integration
const editingKey = computed(() => templateStore.editingSessionToken)
const editorInstance = ref<Editor | null>(null)
const signingKey = computed(() => documentStore.signingSessionToken)
const signingInstance = ref<Signing | null>(null)
const signingError = ref<string | null>(null)
const editorError = ref<string | null>(null)

watch(signingKey, (token) => {
  if (!token) return
  ;(async () => {
    const mainContainer = document.getElementById('signing-main')
    if (!mainContainer) return

    let existing = document.getElementById('signing-container')
    if (existing) mainContainer.removeChild(existing)

    const container = document.createElement('div')
    container.id = 'signing-container'
    container.className = 'full-screen-editor'

    mainContainer.appendChild(container)

    signingInstance.value = new Signing(
      'signing-container',
      {
        width: '100%',
        height: '100%',
        sessionId: token,
      },
      { region: 'com' },
    )

    await signingInstance.value.open()
  })()
})

watch(editingKey, (token) => {
  if (!token) return
  ;(async () => {
    const mainContainer = document.getElementById('editor-main')
    if (!mainContainer) return

    let existing = document.getElementById('editor-container')
    if (existing) mainContainer.removeChild(existing)

    const container = document.createElement('div')
    container.id = 'editor-container'
    container.className = 'full-screen-editor'

    mainContainer.appendChild(container)

    editorInstance.value = new Editor('editor-container', {
      width: '100%',
      height: '100%',
      token: token,
      fieldPlacementOnly: false, // show blocks
      blocks: {
        [BlockKind.TEXT]: { visible: true }, // allow text blocks
      },
    })

    await editorInstance.value.open()
  })()
})

const closeSigning = async () => {
  if (!signingInstance.value) return

  signingInstance.value.destroy()
  documentStore.signingSessionToken = null
  notificationStore.enqueue(
    'success',
    'The signature has been submitted and is currently being processed. Please allow a few minutes for the status to update',
  )
  await documentStore.fetchDocuments(props.investor_type, props.investor_id)
}

const closeEditor = async () => {
  if (!editorInstance.value) return

  editorInstance.value.destroy()
  templateStore.editingSessionToken = null

  // Refresh cache and fetch templates
  if (templateStore.new_template_uuid) {
    await templateStore.refreshTemplateCache(templateStore.new_template_uuid)
    templateStore.new_template_uuid = null
  }
  await templateStore.fetchTemplates(props.investor_type, props.investor_id)

  // Reset state
  editingTemplateId.value = null
  editingTemplateName.value = ''
  isNewTemplate.value = false
}

const canSignPandaDoc = (item) => {
  console.log('canSignPandaDoc', item)
  return can('document', 'sign', item) && item.status === 'awaiting' && item.provider === 'pandadoc'
}

onMounted(async () => {
  // Fetch entity if not already in store (needed for entity admin check)
  if (!currentEntity.value && !['individual', 'funding-entity', 'group', 'all'].includes(props.investor_type)) {
    await entityStore.fetchEntityInfo({ type: props.investor_type, id: String(props.investor_id) })
  }

  await documentStore.fetchDocuments(props.investor_type, props.investor_id)

  if (can('template', 'view')) {
    await templateStore.fetchTemplates(props.investor_type, props.investor_id)
  }

  const document = documentStore.items.get(parseInt(route.query.sign_document_id as string))
  if (document && canSignPandaDoc(document)) {
    documentStore.createDocumentSigningSession(document.id)
  }

  internal_skeleton.value = false
})

const initialState = {
  name: '',
  email_message: '',
  templateable_type: props.investor_type,
  templateable_id: props.investor_id,
  provider: 'pandadoc',
}

const uploader = ref({
  accept: ['.pdf'],
  state: false,
  files: [] as File[],
})

const signing_template = ref({ ...initialState })
const payload = { signing_template: signing_template.value }
const modal = useModal()
const loading = ref(false)
const loadingOnboardingTemplateIds = ref<number[]>([])
const loadingReminderSignatureIds = ref<number[]>([])

// Track the template being edited (for rename on close)
const editingTemplateId = ref<number | null>(null)
const editingTemplateName = ref<string>('')
const isNewTemplate = ref(false)

// Edit Details Modal
const editDetailsModal = useModal()
const editingDetailsTemplate = ref<any>(null)

const openEditDetailsModal = (item: any) => {
  const template = templateStore.template_items.get(item.id)
  if (!template) return
  editingDetailsTemplate.value = template
  editDetailsModal.open()
}

const handleSaveTemplateDetails = async (data: { name: string; email_message: string }) => {
  if (!editingDetailsTemplate.value) return
  await templateStore.updateTemplate(editingDetailsTemplate.value.id, data)
  await templateStore.fetchTemplates(props.investor_type, props.investor_id)
  editDetailsModal.close()
  editingDetailsTemplate.value = null
}

const openTemplateEditor = (item: any) => {
  const template = templateStore.template_items.get(item.id)
  if (!template) return
  editingTemplateId.value = item.id
  editingTemplateName.value = template.name || ''
  isNewTemplate.value = false
  templateStore.createTemplateEditingSession(item.provider_template_id)
}

const submitTemplateForm = async () => {
  loading.value = true
  const selectedFiles = uploader.value?.files
  editingTemplateName.value = payload.signing_template.name || ''
  editingTemplateId.value = null // Will be set after creation via new_template_uuid
  isNewTemplate.value = true

  await templateStore.uploadFile(payload, selectedFiles || [])
  loading.value = false
  modal.close()
}
</script>

<template>
  <VModal :modalStore="modal">
    <template #main>
      <VSection label="Build Template">
        <form @submit.prevent="submitTemplateForm" class="space-y-6">
          <VTextField
            v-model="payload.signing_template.name"
            :label="capitalize('template name')"
            property="template_name"
            ref="autofocus"
            type="text"
          />

          <VTextArea
            v-model="payload.signing_template.email_message"
            :label="capitalize('email message')"
            property="email_message"
          />

          <!-- Drag & Drop Upload Component -->
          <TheUploader
            :accept="uploader.accept"
            :disabled="uploader.state"
            @chosen="
              (f) => {
                uploader.files.splice(0, uploader.files.length, ...f)
              }
            "
          />
        </form>
      </VSection>
    </template>

    <template #footer>
      <VButton :click="submitTemplateForm" class="w-full" size="lg" :loading="loading" variant="primary"
        >Open and prepare the document</VButton
      >
    </template>
  </VModal>

  <div>
    <!-- Signing overlay -->
    <EmbeddedSigningModal
      embedTarget="signing-main"
      :showModal="signingKey !== null"
      @closeEditor="closeSigning"
      :error="signingError"
    />

    <!-- Editor overlay -->
    <EmbeddedEditorModal
      embedTarget="editor-main"
      :showModal="editingKey !== null"
      :templateName="editingTemplateName"
      @closeEditor="closeEditor"
      :error="editorError"
    />

    <!-- Edit Template Details Modal -->
    <EditTemplateDetailsModal
      :modalStore="editDetailsModal"
      :template="editingDetailsTemplate"
      :onSave="handleSaveTemplateDetails"
    />

    <!-- Buttons -->
    <div class="-mt-7 mb-5 flex justify-end gap-2" v-if="can('template', 'request-signature')">
      <RouterLink :to="requestSignatureUrl">
        <VButton size="md" variant="v-blue"> Request Signatures </VButton>
      </RouterLink>
      <VButton :click="() => modal.open()" size="md" variant="v-blue">Build Template</VButton>
    </div>

    <!-- Templates Table -->
    <VTable
      :columns="[
        {
          key: 'display_name',
          name: capitalize(t('shared.template', 0)),
          type: 'string',
          fixed: true,
          is_visible: true,
        },
        { key: 'usage', name: capitalize(t('shared.usage')), type: 'number', align: 'right', is_visible: true },
        {
          key: 'date',
          name: capitalize(t('shared.date')),
          sorted: true,
          type: 'date',
          is_visible: true,
        },
        { key: 'actions', name: '', type: 'actions', align: 'right', is_visible: true },
      ]"
      :items="templates"
      :name="`${props.investor_type}-${props.investor_id}-templates`"
      :skeleton="skeleton"
      :slots="['actions', 'display_name']"
      v-if="can('template', 'view')"
    >
      <template #display_name="{ item }">
        <div class="flex items-center gap-2" :class="{ 'opacity-50': isTemplateDisabled(item) }">
          <span>{{ item.display_name }}</span>
          <span
            v-if="templateStore.template_items.get(item.id)?.is_global"
            class="rounded-full bg-purple-100 px-2 py-0.5 text-xs text-purple-700"
          >
            Global
          </span>
          <span v-if="isTemplateDisabled(item)" class="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
            Requires completed Series Supplement
          </span>
          <VBadge v-if="item.provider === 'hellosign'" class="ml-3" size="xs" color="gray">Legacy</VBadge>
        </div>
      </template>
      <template #actions="{ item }">
        <ActionsMenu :class="{ 'opacity-50': isTemplateDisabled(item) }">
          <ActionsGroup>
            <!-- Regular Send for non-global templates -->
            <ActionItem
              v-if="can('template', 'action-use') && !templateStore.template_items.get(item.id)?.is_global"
              :text="capitalize(t('shared.send'))"
              :to="generateUseTemplateUrl(item)"
              tag="RouterLink"
            />
            <!-- Global template with dependencies (Investor Packet) - uses normal document flow -->
            <ActionItem
              v-else-if="
                templateStore.template_items.get(item.id)?.is_global &&
                templateStore.template_items.get(item.id)?.dependency_template_ids?.length &&
                isDependencySatisfied(item)
              "
              :text="capitalize(t('shared.send'))"
              :to="generateUseTemplateUrl(item)"
              tag="RouterLink"
            />
            <!-- Global template with dependencies - disabled state -->
            <ActionItem
              v-else-if="
                templateStore.template_items.get(item.id)?.is_global &&
                templateStore.template_items.get(item.id)?.dependency_template_ids?.length &&
                !isDependencySatisfied(item)
              "
              :text="capitalize(t('shared.send'))"
              :disabled="true"
            />
            <!-- Global template without dependencies (Series Supplement, AML) - Site Admin only -->
            <ActionItem
              v-else-if="
                templateStore.template_items.get(item.id)?.is_global &&
                !templateStore.template_items.get(item.id)?.dependency_template_ids?.length &&
                authStore.is_site_admin
              "
              :text="capitalize(t('shared.send'))"
              :disabled="hasAwaitingDocument(item)"
              :loading="loadingOnboardingTemplateIds.includes(item.id)"
              @click="() => createOnboardingDocument(item)"
            />
            <!-- Fallback when no actions available -->
            <ActionItem v-else text="These documents cannot be edited" :disabled="true" />
            <!-- Edit Details - for editing name and note (PandaDoc only) -->
            <ActionItem
              v-if="
                can('template', 'action-edit') &&
                item.provider === 'pandadoc' &&
                !templateStore.template_items.get(item.id)?.is_global
              "
              text="Edit Details"
              @click="() => openEditDetailsModal(item)"
            />
            <!-- Edit Template - for PandaDoc editor -->
            <ActionItem
              v-if="
                can('template', 'action-edit') &&
                item.provider === 'pandadoc' &&
                !templateStore.template_items.get(item.id)?.is_global
              "
              text="Edit Template"
              @click="() => openTemplateEditor(item)"
            />
            <!-- Disabled Edit for HelloSign (legacy) templates -->
            <ActionItem
              v-if="
                can('template', 'action-edit') &&
                item.provider === 'hellosign' &&
                !templateStore.template_items.get(item.id)?.is_global
              "
              :text="capitalize(t('shared.edit'))"
              :disabled="true"
              tooltip="This template can't be edited. Please create a new template"
            />
          </ActionsGroup>
          <ActionsGroup
            v-if="can('template', 'action-delete') && !templateStore.template_items.get(item.id)?.is_global"
          >
            <ActionItem :text="capitalize(t('shared.delete'))" @click="() => removeTemplate(item)" />
          </ActionsGroup>
        </ActionsMenu>
      </template>
    </VTable>

    <!-- Document Type Buttons -->
    <div class="mb-3 mt-10 flex items-center space-x-1.5">
      <VButton :active="selectedDocumentType === 'in-progress'" class="w-36" @click="selectDocumentType('in-progress')">
        <span>{{ capitalize(t('shared.in-progress')) }}</span>
        <span
          class="ml-2 rounded-full px-2 py-0.5 text-xs text-black"
          :class="[selectedDocumentType === 'in-progress' ? 'bg-sky-100' : 'bg-gray-100']"
        >
          {{ document_count.in_progress }}
        </span>
      </VButton>
      <VButton :active="selectedDocumentType === 'completed'" class="w-36" @click="selectDocumentType('completed')">
        <span>{{ capitalize(t('shared.completed')) }}</span>
        <span
          class="ml-2 rounded-full px-2 py-0.5 text-xs text-black"
          :class="[selectedDocumentType === 'completed' ? 'bg-sky-100' : 'bg-gray-100']"
        >
          {{ document_count.completed }}
        </span>
      </VButton>
      <VButton :active="selectedDocumentType === 'voided'" class="w-36" @click="selectDocumentType('voided')">
        <span>{{ capitalize(t('shared.voided')) }}</span>
        <span
          class="ml-2 rounded-full px-2 py-0.5 text-xs text-black"
          :class="[selectedDocumentType === 'voided' ? 'bg-sky-100' : 'bg-gray-100']"
        >
          {{ document_count.voided }}
        </span>
      </VButton>
    </div>

    <!-- Documents Table -->
    <VTable
      :columns="[
        {
          key: 'name',
          name: capitalize(t('shared.document', 0)),
          type: 'string',
          fixed: true,
          is_visible: true,
        },
        { key: 'status', name: capitalize(t('shared.status')), type: 'string', align: 'left', is_visible: true },
        {
          key: 'date',
          name: capitalize(t('shared.date')),
          sorted: true,
          type: 'date',
          is_visible: true,
        },
        { key: 'actions', name: '', type: 'actions', align: 'right', is_visible: true },
      ]"
      :items="currentDocuments"
      :name="`${props.investor_type}-${props.investor_id}-documents`"
      :skeleton="skeleton"
      :slots="['actions', 'name', 'status']"
      sub_item_key="signatures"
      :expand="true"
    >
      <template #name="{ item }">
        <div class="flex items-center gap-2">
          <div class="mb-1 font-medium" v-if="!!item.name" v-html="item.name"></div>
          <VBadge v-if="item.provider === 'hellosign'" class="ml-3" size="xs" color="gray">Legacy</VBadge>
        </div>
        <div class="text-xs text-gray-500">To: {{ getSigners(item) }}</div>
      </template>
      <template #subgroup.name="{ item }">
        <template v-if="can('investor', 'view')">
          <RouterLink
            v-if="item.investor._custom_type === null && item.signer._custom_type !== null"
            class="text-[#3b88af] underline decoration-[#3b88af]/50 hover:text-gray-900 hover:decoration-gray-900/50"
            :to="{ name: 'investing.individual-overview', params: { individual_id: item.signer.id } }"
          >
            {{ item.signer.name }}
          </RouterLink>
          <RouterLink
            v-else-if="item.investor._custom_type === 'individual'"
            class="text-[#3b88af] underline decoration-[#3b88af]/50 hover:text-gray-900 hover:decoration-gray-900/50"
            :to="{ name: 'investing.individual-overview', params: { individual_id: item.investor.id } }"
          >
            {{ item.investor.name }}
          </RouterLink>
          <RouterLink
            v-else-if="item.investor._custom_type === 'funding-entity'"
            class="text-[#3b88af] underline decoration-[#3b88af]/50 hover:text-gray-900 hover:decoration-gray-900/50"
            :to="{ name: 'investing.other-entity.overview', params: { other_entity_id: item.investor.id } }"
          >
            {{ item.investor.name }} - {{ item.signer.name }}
          </RouterLink>
          <RouterLink
            v-else
            class="text-[#3b88af] underline decoration-[#3b88af]/50 hover:text-gray-900 hover:decoration-gray-900/50"
            :to="{
              name: 'investing.entity-overview',
              params: { entity_id: item.investor.id, entity_type: item.investor._custom_type },
            }"
          >
            {{ item.investor.name }} - {{ item.signer.name }}
          </RouterLink>
        </template>
        <template v-else>
          {{ item.investor?.name || item.signer?.name }}
        </template>
      </template>
      <template #status="{ item }"> {{ getDocumentStatus(item) }} </template>
      <template #subgroup.status="{ item }">
        {{ item.status }}
      </template>
      <template #actions="{ item }">
        <div class="flex items-center gap-2">
          <VButton
            v-if="canSignPandaDoc(item)"
            :click="() => documentStore.createDocumentSigningSession(item.id)"
            size="xs-narrow"
            class="!px-1.5 !py-0.5"
            tooltip="Sign"
          >
            Sign
          </VButton>
          <ActionsMenu v-if="can('document', 'view', item) && item.status !== 'voided'">
            <ActionsGroup>
              <ActionItem
                v-if="can('document', 'view', item) && item.status !== 'voided' && item.provider === 'hellosign'"
                :text="capitalize(t('shared.download'))"
                @click="() => documentStore.downloadDocument(item.id)"
              />
              <ActionItem
                v-if="can('document', 'view', item) && item.status !== 'voided' && item.provider === 'pandadoc'"
                :text="capitalize(t('shared.download'))"
                @click="() => documentStore.pandaDocDownloadDocument(item.id)"
              />
            </ActionsGroup>
            <ActionsGroup v-if="can('document', 'void') && item.status === 'awaiting'">
              <ActionItem
                v-if="item.provider === 'pandadoc'"
                :text="capitalize(t('shared.void'))"
                @click="() => voidDocument(item)"
              />
              <ActionItem
                v-if="item.provider === 'hellosign'"
                :text="capitalize(t('shared.void'))"
                :disabled="true"
                tooltip="This document can't be voided. Legacy documents must be managed through HelloSign"
              />
            </ActionsGroup>
          </ActionsMenu>
        </div>
      </template>
      <template #subgroup.actions="{ item }">
        <div class="flex justify-end">
          <ActionsMenu v-if="item.status_code === 'awaiting_signature' && item.status !== 'Signed'">
            <ActionsGroup>
              <ActionItem
                :text="capitalize(t('shared.send reminder'))"
                :loading="loadingReminderSignatureIds.includes(item.id)"
                :disabled="item.signing_order && !item.current_signer"
                :tooltip="
                  item.signing_order && !item.current_signer
                    ? 'This signer is not next in the signing order'
                    : undefined
                "
                @click="() => signatureSendReminder(item)"
              />
            </ActionsGroup>
          </ActionsMenu>
        </div>
      </template>
    </VTable>
  </div>
</template>

<style scoped>
:global(.full-screen-editor) {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background: white;
}

:global(#signing-container),
:global(#editor-container) {
  width: 100%;
  height: 100%;
}
</style>
